import { mat4, quat, vec3 } from 'gl-matrix';
import { AnimationFrame } from '../AnimationFrame';

/**
 * Build override joint matrices for a two-bone IK chain from the solver's
 * output positions. Applies delta rotations directly to the original matrices
 * to preserve scale exactly.
 */
export function buildChainOverrides(
  rootFrame: AnimationFrame,
  midFrame: AnimationFrame,
  tipFrame: AnimationFrame,
  newMidPos: vec3,
  newTipPos: vec3,
): { rootMatrix: mat4; midMatrix: mat4; tipMatrix: mat4; midDelta: quat } {
  const origRootWorld = rootFrame.jointMatrix;
  const origMidWorld = midFrame.jointMatrix;
  const origTipWorld = tipFrame.jointMatrix;

  // Original positions and bone directions
  const rootPos = mat4.getTranslation(vec3.create(), origRootWorld);
  const midPos = mat4.getTranslation(vec3.create(), origMidWorld);
  const tipPos = mat4.getTranslation(vec3.create(), origTipWorld);

  const origRootToMid = vec3.normalize(vec3.create(), vec3.sub(vec3.create(), midPos, rootPos));
  const origMidToTip = vec3.normalize(vec3.create(), vec3.sub(vec3.create(), tipPos, midPos));

  // New bone directions
  const newRootToMid = vec3.normalize(vec3.create(), vec3.sub(vec3.create(), newMidPos, rootPos));
  const newMidToTip = vec3.normalize(vec3.create(), vec3.sub(vec3.create(), newTipPos, newMidPos));

  // Delta rotations
  const rootDelta = quatFromVectors(origRootToMid, newRootToMid);
  const midDelta = quatFromVectors(origMidToTip, newMidToTip);

  // Apply delta rotation to original matrix (preserves scale exactly).
  // deltaRotMat * origMatrix rotates the columns without touching scale.
  const rootMatrix = applyDeltaRotation(origRootWorld, rootDelta, rootPos);
  const midMatrix = applyDeltaRotation(origMidWorld, midDelta, newMidPos);
  const tipMatrix = applyDeltaTranslation(origTipWorld, newTipPos);

  return { rootMatrix, midMatrix, tipMatrix, midDelta };
}

/**
 * Apply a delta rotation to a joint matrix, keeping its scale intact.
 * Moves the origin to `pos`, rotates the upper-3x3 by `delta`.
 */
function applyDeltaRotation(orig: mat4, delta: quat, pos: vec3): mat4 {
  const deltaM = mat4.fromQuat(mat4.create(), delta);
  const result = mat4.multiply(mat4.create(), deltaM, orig);
  // Restore position (the multiply shifts it)
  result[12] = pos[0];
  result[13] = pos[1];
  result[14] = pos[2];
  return result;
}

/**
 * Copy a joint matrix but move it to a new position.
 * The upper-3x3 (rotation+scale) is preserved exactly.
 */
function applyDeltaTranslation(orig: mat4, pos: vec3): mat4 {
  const result = mat4.clone(orig);
  result[12] = pos[0];
  result[13] = pos[1];
  result[14] = pos[2];
  return result;
}

/** Quaternion that rotates unit vector `from` to unit vector `to`. */
function quatFromVectors(from: vec3, to: vec3): quat {
  const dot = vec3.dot(from, to);
  if (dot > 0.99999) return quat.create();
  if (dot < -0.99999) {
    let perp = vec3.cross(vec3.create(), from, vec3.fromValues(1, 0, 0));
    if (vec3.length(perp) < 0.0001) {
      perp = vec3.cross(vec3.create(), from, vec3.fromValues(0, 1, 0));
    }
    vec3.normalize(perp, perp);
    return quat.setAxisAngle(quat.create(), perp, Math.PI);
  }
  const axis = vec3.cross(vec3.create(), from, to);
  const q = quat.fromValues(axis[0], axis[1], axis[2], 1 + dot);
  return quat.normalize(q, q);
}
